import { drawPath } from "@/components/expression/svgUtils/drawPath";
import { useNodePoints } from "@/components/expression/svgUtils/useNodePoints";
import { groupsJson } from "@/components/suggestions/providers/data";

import type { NodePoints, Point, ViewBox } from "@/components/expression/svgUtils/types";
import type { GroupTypeValue } from "@/components/suggestions/providers/data/types";
import type { NodeModel } from "@/models/nodes/types";
import type { OpeningSymbol, ClosingSymbol } from "@corca/data";

const DEFAULT_BRACKET_WIDTH = 5;

type SymbolPoints = {
  startPoint: Point;
  endPoint: Point;
};

type SymbolPath = {
  path: string;
  points: SymbolPoints;
};

const bracketsSvgPaths: {
  [T in GroupTypeValue]: {
    path: (
      nodePoints: NodePoints,
      addBracketWidth?: number,
    ) => { left: SymbolPath; right: SymbolPath; bracketWidth: number; padding?: number };
    staticWidth: number;
  };
} = {
  round: {
    path: (nodePoints) => {
      const { y: height } = nodePoints.bl;

      /**
       * After this threshold we draw vertically stretched brackets
       */
      const threshold = 50;

      /**
       * We stop arc at this point for brackets higher than `threshold`.
       * For bottom point we subtract `arcStopShift` from bottom `y` point (e.g. `nodePoints.lb.y - arcStopShift`).
       */
      const arcStopShift = 13;
      const pullShift = 0.6;
      const widthMultiplier = 1;
      const minBracketWidth = 2;
      const stretchedBracketsWidth = 4;

      const aboveThreshold = height > threshold;

      const bracketWidth = aboveThreshold
        ? stretchedBracketsWidth
        : minBracketWidth + (height * widthMultiplier) / 10;

      const leftStartPoint: Point = {
        x: nodePoints.tl.x + bracketWidth,
        y: nodePoints.tl.y,
      };

      const leftEndPoint: Point = {
        x: nodePoints.bl.x + bracketWidth,
        y: nodePoints.bl.y,
      };

      const leftTopPoint: Point = {
        x: nodePoints.lc.x,
        y: aboveThreshold ? arcStopShift : nodePoints.lc.y,
      };

      const leftBottomPoint: Point = {
        x: nodePoints.lc.x,
        y: aboveThreshold ? nodePoints.lb.y - arcStopShift : nodePoints.lc.y,
      };

      const leftPath = drawPath()
        .moveTo(leftStartPoint)
        .arcTo(leftStartPoint, leftTopPoint, {
          x: nodePoints.tla.x,
          y: bracketWidth + pullShift,
        })
        .lineTo(leftBottomPoint)
        .arcTo(leftBottomPoint, leftEndPoint, {
          x: nodePoints.bla.x,
          y: leftEndPoint.y - bracketWidth - pullShift,
        })
        .finish();

      const rightStartPoint: Point = {
        x: nodePoints.tr.x - bracketWidth,
        y: nodePoints.tr.y,
      };

      const rightEndPoint: Point = {
        x: nodePoints.br.x - bracketWidth,
        y: nodePoints.br.y,
      };

      const rightTopPoint: Point = {
        x: nodePoints.rc.x,
        y: aboveThreshold ? arcStopShift : nodePoints.rc.y,
      };

      const rightBottomPoint: Point = {
        x: nodePoints.rc.x,
        y: aboveThreshold ? nodePoints.rb.y - arcStopShift : nodePoints.rc.y,
      };

      const rightPath = drawPath()
        .moveTo(rightStartPoint)
        .arcTo(rightStartPoint, rightTopPoint, {
          x: nodePoints.tra.x,
          y: nodePoints.tr.y + bracketWidth + pullShift,
        })
        .lineTo(rightBottomPoint)
        .arcTo(rightBottomPoint, rightEndPoint, {
          x: nodePoints.bra.x,
          y: rightEndPoint.y - bracketWidth - pullShift,
        })
        .finish();

      const left: SymbolPath = {
        path: leftPath,
        points: {
          startPoint: leftStartPoint,
          endPoint: leftEndPoint,
        },
      };

      const right: SymbolPath = {
        path: rightPath,
        points: {
          startPoint: rightStartPoint,
          endPoint: rightEndPoint,
        },
      };

      return {
        left,
        right,
        bracketWidth,
      };
    },
    staticWidth: DEFAULT_BRACKET_WIDTH,
  },
  square: {
    path(nodePoints, addBracketWidth = 0) {
      const { staticWidth } = this;
      const bracketWidth = staticWidth + addBracketWidth;
      const leftStartPoint: Point = {
        x: nodePoints.tl.x + bracketWidth,
        y: nodePoints.tl.y,
      };
      const leftEndPoint: Point = {
        x: nodePoints.bl.x + bracketWidth,
        y: nodePoints.bl.y,
      };
      const rightStartPoint: Point = {
        x: nodePoints.tr.x - bracketWidth,
        y: nodePoints.tr.y,
      };
      const rightEndPoint: Point = {
        x: nodePoints.br.x - bracketWidth,
        y: nodePoints.br.y,
      };

      const leftPath = drawPath()
        .moveTo(leftStartPoint)
        .lineTo(nodePoints.tl)
        .lineTo(nodePoints.bl)
        .lineTo(leftEndPoint)
        .finish();

      const rightPath = drawPath()
        .moveTo(rightStartPoint)
        .lineTo(nodePoints.tr)
        .lineTo(nodePoints.br)
        .lineTo(rightEndPoint)
        .finish();

      const left: SymbolPath = {
        path: leftPath,
        points: {
          startPoint: leftStartPoint,
          endPoint: leftEndPoint,
        },
      };

      const right: SymbolPath = {
        path: rightPath,
        points: {
          startPoint: rightStartPoint,
          endPoint: rightEndPoint,
        },
      };

      return {
        left,
        right,
        bracketWidth,
      };
    },
    staticWidth: 4,
  },
  "open-interval": {
    path(nodePoints) {
      const { staticWidth: bracketWidth } = this;
      const leftStartPoint: Point = {
        x: nodePoints.tl.x + bracketWidth,
        y: nodePoints.tl.y,
      };
      const leftEndPoint: Point = {
        x: nodePoints.bl.x + bracketWidth,
        y: nodePoints.bl.y,
      };
      const rightStartPoint: Point = {
        x: nodePoints.tr.x - bracketWidth,
        y: nodePoints.tr.y,
      };
      const rightEndPoint: Point = {
        x: nodePoints.br.x - bracketWidth,
        y: nodePoints.br.y,
      };

      const leftPath = drawPath()
        .moveTo(leftStartPoint)
        .lineTo(nodePoints.tl)
        .moveTo(leftStartPoint)
        .lineTo(leftEndPoint)
        .moveTo(nodePoints.bl)
        .lineTo(leftEndPoint)
        .finish();

      const rightPath = drawPath()
        .moveTo(rightStartPoint)
        .lineTo(nodePoints.tr)
        .moveTo(rightStartPoint)
        .lineTo(rightEndPoint)
        .lineTo(nodePoints.br)
        .finish();

      const left: SymbolPath = {
        path: leftPath,
        points: {
          startPoint: leftStartPoint,
          endPoint: leftEndPoint,
        },
      };

      const right: SymbolPath = {
        path: rightPath,
        points: {
          startPoint: rightStartPoint,
          endPoint: rightEndPoint,
        },
      };

      return {
        left,
        right,
        bracketWidth,
        padding: bracketWidth * 2,
      };
    },
    staticWidth: 4,
  },
  curly: {
    path(nodePoints) {
      const { staticWidth: bracketWidth } = this;
      const arcStopShift = 5;

      const leftStartPoint: Point = {
        x: nodePoints.tl.x + bracketWidth,
        y: nodePoints.tl.y,
      };
      const leftTopPoint: Point = {
        x: nodePoints.tl.x + bracketWidth / 2,
        y: arcStopShift,
      };
      const leftTopCenterPoint: Point = {
        x: nodePoints.lc.x + bracketWidth / 2,
        y: nodePoints.lc.y - arcStopShift,
      };
      const leftBottomCenterPoint: Point = {
        x: nodePoints.lc.x + bracketWidth / 2,
        y: nodePoints.lc.y + arcStopShift,
      };
      const leftBottomPoint: Point = {
        x: nodePoints.lc.x + bracketWidth / 2,
        y: nodePoints.lb.y - arcStopShift,
      };
      const leftEndPoint: Point = {
        x: nodePoints.bl.x + bracketWidth,
        y: nodePoints.bl.y,
      };

      const leftPath = drawPath()
        .moveTo(leftStartPoint)
        .arcTo(leftStartPoint, leftTopPoint, {
          x: nodePoints.lt.x + bracketWidth / 2,
          y: nodePoints.lt.y,
        })
        .lineTo(leftTopCenterPoint)
        .arcTo(leftTopCenterPoint, nodePoints.lc, {
          x: nodePoints.lc.x + bracketWidth / 2,
          y: nodePoints.lc.y,
        })
        .arcTo(nodePoints.lc, leftBottomCenterPoint, {
          x: nodePoints.lc.x + bracketWidth / 2,
          y: nodePoints.lc.y,
        })
        .lineTo(leftBottomPoint)
        .arcTo(leftBottomPoint, leftEndPoint, {
          x: nodePoints.lt.x + bracketWidth / 2,
          y: leftEndPoint.y,
        })
        .finish();

      const rightStartPoint: Point = {
        x: nodePoints.tr.x - bracketWidth,
        y: nodePoints.tr.y,
      };
      const rightTopPoint: Point = {
        x: nodePoints.tr.x - bracketWidth / 2,
        y: arcStopShift,
      };
      const rightTopCenterPoint: Point = {
        x: nodePoints.rc.x - bracketWidth / 2,
        y: nodePoints.rc.y - arcStopShift,
      };
      const rightBottomCenterPoint: Point = {
        x: nodePoints.rc.x - bracketWidth / 2,
        y: nodePoints.rc.y + arcStopShift,
      };
      const rightBottomPoint: Point = {
        x: nodePoints.rc.x - bracketWidth / 2,
        y: nodePoints.rb.y - arcStopShift,
      };
      const rightEndPoint: Point = {
        x: nodePoints.br.x - bracketWidth,
        y: nodePoints.br.y,
      };

      const rightPath = drawPath()
        .moveTo(rightStartPoint)
        .arcTo(rightStartPoint, rightTopPoint, {
          x: nodePoints.rt.x - bracketWidth / 2,
          y: nodePoints.rt.y,
        })
        .lineTo(rightTopCenterPoint)
        .arcTo(rightTopCenterPoint, nodePoints.rc, {
          x: nodePoints.rc.x - bracketWidth / 2,
          y: nodePoints.rc.y,
        })
        .arcTo(nodePoints.rc, rightBottomCenterPoint, {
          x: nodePoints.rc.x - bracketWidth / 2,
          y: nodePoints.rc.y,
        })
        .lineTo(rightBottomPoint)
        .arcTo(rightBottomPoint, rightEndPoint, {
          x: nodePoints.rt.x - bracketWidth / 2,
          y: rightEndPoint.y,
        })
        .finish();

      const left: SymbolPath = {
        path: leftPath,
        points: {
          startPoint: leftStartPoint,
          endPoint: leftEndPoint,
        },
      };

      const right: SymbolPath = {
        path: rightPath,
        points: {
          startPoint: rightStartPoint,
          endPoint: rightEndPoint,
        },
      };

      return {
        left,
        right,
        bracketWidth,
        padding: bracketWidth + 2,
      };
    },
    staticWidth: 6.5,
  },
  angle: {
    path: (nodePoints) => {
      const { y: height } = nodePoints.bl;

      const minBracketWidth = 3;
      const maxBracketWidth = 10;

      const mulipliedWidth = Math.floor(minBracketWidth + (height * 2) / 10 - 1);
      const bracketWidth = mulipliedWidth > maxBracketWidth ? maxBracketWidth : mulipliedWidth;

      const leftStartPoint: Point = {
        x: nodePoints.tl.x + bracketWidth,
        y: nodePoints.tl.y,
      };
      const leftMiddlePoint: Point = {
        x: nodePoints.lc.x,
        y: nodePoints.lc.y,
      };
      const leftEndPoint: Point = {
        x: nodePoints.bl.x + bracketWidth,
        y: nodePoints.bl.y,
      };

      const leftPath = drawPath()
        .moveTo(leftStartPoint)
        .lineTo(leftMiddlePoint)
        .lineTo(leftEndPoint)
        .finish();

      const rightStartPoint: Point = {
        x: nodePoints.tr.x - bracketWidth,
        y: nodePoints.tr.y,
      };
      const rightMiddlePoint: Point = {
        x: nodePoints.rc.x,
        y: nodePoints.rc.y,
      };
      const rightEndPoint: Point = {
        x: nodePoints.br.x - bracketWidth,
        y: nodePoints.br.y,
      };

      const rightPath = drawPath()
        .moveTo(rightStartPoint)
        .lineTo(rightMiddlePoint)
        .lineTo(rightEndPoint)
        .finish();

      const left: SymbolPath = {
        path: leftPath,
        points: {
          startPoint: leftStartPoint,
          endPoint: leftEndPoint,
        },
      };

      const right: SymbolPath = {
        path: rightPath,
        points: {
          startPoint: rightStartPoint,
          endPoint: rightEndPoint,
        },
      };

      return {
        left,
        right,
        bracketWidth,
        padding: bracketWidth + 2,
      };
    },
    staticWidth: 6,
  },
  bar: {
    path: (nodePoints) => {
      const leftStartPoint: Point = {
        x: nodePoints.tl.x,
        y: nodePoints.tl.y,
      };
      const leftEndPoint: Point = {
        x: nodePoints.bl.x,
        y: nodePoints.bl.y,
      };

      const leftPath = drawPath().moveTo(leftStartPoint).lineTo(leftEndPoint).finish();

      const rightStartPoint: Point = {
        x: nodePoints.tr.x,
        y: nodePoints.tr.y,
      };
      const rightEndPoint: Point = {
        x: nodePoints.br.x,
        y: nodePoints.br.y,
      };

      const rightPath = drawPath().moveTo(rightStartPoint).lineTo(rightEndPoint).finish();

      const left: SymbolPath = {
        path: leftPath,
        points: {
          startPoint: leftStartPoint,
          endPoint: leftEndPoint,
        },
      };

      const right: SymbolPath = {
        path: rightPath,
        points: {
          startPoint: rightStartPoint,
          endPoint: rightEndPoint,
        },
      };

      return {
        left,
        right,
        bracketWidth: 1,
        padding: 4,
      };
    },
    staticWidth: 4,
  },
  "double-bar": {
    path(nodePoints) {
      const { staticWidth: bracketWidth } = this;

      const leftStartPoint: Point = {
        x: nodePoints.tl.x,
        y: nodePoints.tl.y,
      };
      const leftEndPoint: Point = {
        x: nodePoints.bl.x,
        y: nodePoints.bl.y,
      };
      const leftSecondBarStartPoint: Point = {
        x: leftStartPoint.x + bracketWidth,
        y: leftStartPoint.y,
      };
      const leftSecondBarEndPoint: Point = {
        x: leftEndPoint.x + bracketWidth,
        y: leftEndPoint.y,
      };

      const leftPath = drawPath()
        .moveTo(leftStartPoint)
        .lineTo(leftEndPoint)
        .moveTo(leftSecondBarStartPoint)
        .lineTo(leftSecondBarEndPoint)
        .finish();

      const rightStartPoint: Point = {
        x: nodePoints.tr.x,
        y: nodePoints.tr.y,
      };
      const rightEndPoint: Point = {
        x: nodePoints.br.x,
        y: nodePoints.br.y,
      };
      const rightSecondBarStartPoint: Point = {
        x: rightStartPoint.x - bracketWidth,
        y: rightStartPoint.y,
      };
      const rightSecondBarEndPoint: Point = {
        x: rightEndPoint.x - bracketWidth,
        y: rightEndPoint.y,
      };

      const rightPath = drawPath()
        .moveTo(rightSecondBarStartPoint)
        .lineTo(rightSecondBarEndPoint)
        .moveTo(rightStartPoint)
        .lineTo(rightEndPoint)
        .finish();

      const left: SymbolPath = {
        path: leftPath,
        points: {
          startPoint: leftStartPoint,
          endPoint: leftEndPoint,
        },
      };

      const right: SymbolPath = {
        path: rightPath,
        points: {
          startPoint: rightStartPoint,
          endPoint: rightEndPoint,
        },
      };

      return {
        left,
        right,
        bracketWidth,
        padding: bracketWidth + 4,
      };
    },
    staticWidth: 3,
  },
  ceiling: {
    path(nodePoints) {
      const { staticWidth: bracketWidth } = this;
      const leftStartPoint: Point = {
        x: nodePoints.tl.x + bracketWidth,
        y: nodePoints.tl.y,
      };
      const rightStartPoint: Point = {
        x: nodePoints.tr.x - bracketWidth,
        y: nodePoints.tr.y,
      };

      const leftPath = drawPath()
        .moveTo(leftStartPoint)
        .lineTo(nodePoints.tl)
        .lineTo(nodePoints.bl)
        .finish();

      const rightPath = drawPath()
        .moveTo(rightStartPoint)
        .lineTo(nodePoints.tr)
        .lineTo(nodePoints.br)
        .finish();

      const left: SymbolPath = {
        path: leftPath,
        points: {
          startPoint: leftStartPoint,
          endPoint: nodePoints.bl,
        },
      };

      const right: SymbolPath = {
        path: rightPath,
        points: {
          startPoint: rightStartPoint,
          endPoint: nodePoints.br,
        },
      };

      return {
        left,
        right,
        bracketWidth,
      };
    },
    staticWidth: 4,
  },
  floor: {
    path(nodePoints) {
      const { staticWidth: bracketWidth } = this;
      const leftEndPoint: Point = {
        x: nodePoints.bl.x + bracketWidth,
        y: nodePoints.bl.y,
      };
      const rightEndPoint: Point = {
        x: nodePoints.br.x - bracketWidth,
        y: nodePoints.br.y,
      };

      const leftPath = drawPath()
        .moveTo(nodePoints.tl)
        .lineTo(nodePoints.bl)
        .lineTo(leftEndPoint)
        .finish();

      const rightPath = drawPath()
        .moveTo(nodePoints.tr)
        .lineTo(nodePoints.br)
        .lineTo(rightEndPoint)
        .finish();

      const left: SymbolPath = {
        path: leftPath,
        points: {
          startPoint: nodePoints.tl,
          endPoint: leftEndPoint,
        },
      };

      const right: SymbolPath = {
        path: rightPath,
        points: {
          startPoint: nodePoints.tr,
          endPoint: rightEndPoint,
        },
      };

      return {
        left,
        right,
        bracketWidth,
      };
    },
    staticWidth: 4,
  },
  moustache: {
    path(nodePoints) {
      const { staticWidth: bracketWidth } = this;
      const arcStopShift = 7;

      const leftStartPoint: Point = {
        x: nodePoints.tl.x + bracketWidth,
        y: nodePoints.tl.y,
      };
      const leftTopPoint: Point = {
        x: nodePoints.lc.x + bracketWidth / 2,
        y: arcStopShift,
      };
      const leftBottomPoint: Point = {
        x: nodePoints.lc.x + bracketWidth / 2,
        y: nodePoints.lb.y - arcStopShift,
      };
      const leftEndPoint: Point = {
        x: nodePoints.bl.x,
        y: nodePoints.bl.y,
      };

      const leftPath = drawPath()
        .moveTo(leftEndPoint)
        .arcTo(leftEndPoint, leftBottomPoint, {
          x: nodePoints.bl.x + bracketWidth / 2,
          y: nodePoints.bl.y,
        })
        .lineTo(leftTopPoint)
        .arcTo(leftTopPoint, leftStartPoint, {
          x: nodePoints.tl.x + bracketWidth / 2,
          y: nodePoints.tl.y,
        })
        .finish();

      const rightStartPoint: Point = {
        x: nodePoints.tr.x - bracketWidth,
        y: nodePoints.tr.y,
      };
      const rightTopPoint: Point = {
        x: nodePoints.rc.x - bracketWidth / 2,
        y: arcStopShift,
      };
      const rightBottomPoint: Point = {
        x: nodePoints.rc.x - bracketWidth / 2,
        y: nodePoints.rb.y - arcStopShift,
      };
      const rightEndPoint: Point = {
        x: nodePoints.br.x,
        y: nodePoints.br.y,
      };

      const rightPath = drawPath()
        .moveTo(rightStartPoint)
        .arcTo(rightStartPoint, rightTopPoint, {
          x: nodePoints.tr.x - bracketWidth / 2,
          y: nodePoints.tr.y,
        })
        .lineTo(rightBottomPoint)
        .arcTo(rightBottomPoint, rightEndPoint, {
          x: nodePoints.br.x - bracketWidth / 2,
          y: nodePoints.br.y,
        })
        .finish();

      const left: SymbolPath = {
        path: leftPath,
        points: {
          startPoint: leftStartPoint,
          endPoint: leftEndPoint,
        },
      };

      const right: SymbolPath = {
        path: rightPath,
        points: {
          startPoint: rightStartPoint,
          endPoint: rightEndPoint,
        },
      };

      return {
        left,
        right,
        bracketWidth,
        padding: bracketWidth,
      };
    },
    staticWidth: 13,
  },
  "double-curly": {
    path: (nodePoints) => {
      const { left, right, ...props } = bracketsSvgPaths.curly.path(nodePoints);

      const leftPath = drawPath(0, 0, left.path)
        .moveTo({
          x: left.points.startPoint.x - 1,
          y: left.points.startPoint.y + 1,
        })
        .lineTo({
          x: left.points.endPoint.x - 1,
          y: left.points.endPoint.y - 1,
        })
        .finish();

      const rightPath = drawPath(0, 0, right.path)
        .moveTo({
          x: right.points.startPoint.x + 1,
          y: right.points.startPoint.y + 1,
        })
        .lineTo({
          x: right.points.endPoint.x + 1,
          y: right.points.endPoint.y - 1,
        })
        .finish();

      return {
        ...props,
        padding: props.bracketWidth + 4,
        left: {
          ...left,
          path: leftPath,
        },
        right: {
          ...right,
          path: rightPath,
        },
      };
    },
    staticWidth: 6.5,
  },
  "double-stroke": {
    path: (nodePoints) => {
      const { left, right, bracketWidth } = bracketsSvgPaths.square.path(nodePoints, 2);

      const leftPath = drawPath(0, 0, left.path)
        .moveTo({
          ...left.points.startPoint,
          x: left.points.startPoint.x - 3,
        })
        .lineTo({
          ...left.points.endPoint,
          x: left.points.endPoint.x - 3,
        })
        .finish();

      const rightPath = drawPath(0, 0, right.path)
        .moveTo({
          ...right.points.startPoint,
          x: right.points.startPoint.x + 3,
        })
        .lineTo({
          ...right.points.endPoint,
          x: right.points.endPoint.x + 3,
        })
        .finish();

      return {
        bracketWidth,
        padding: bracketWidth + 2,
        left: {
          ...left,
          path: leftPath,
        },
        right: {
          ...right,
          path: rightPath,
        },
      };
    },
    staticWidth: 4,
  },
  bra: {
    path: (nodePoints, addBracketWidth = 0) => {
      const { y: height } = nodePoints.bl;

      const minBracketWidth = 3;
      const maxBracketWidth = 10;
      const baseWidth = Math.floor(minBracketWidth + (height * 2) / 10 - 1);
      const bracketWidth = Math.min(maxBracketWidth, baseWidth) + addBracketWidth;

      const leftStart = { x: nodePoints.tl.x + bracketWidth, y: nodePoints.tl.y };
      const leftMid = { x: nodePoints.lc.x, y: nodePoints.lc.y };
      const leftEnd = { x: nodePoints.bl.x + bracketWidth, y: nodePoints.bl.y };

      const leftPath = drawPath().moveTo(leftStart).lineTo(leftMid).lineTo(leftEnd).finish();

      const barPath = drawPath()
        .moveTo({ x: nodePoints.tr.x, y: nodePoints.tr.y })
        .lineTo({ x: nodePoints.br.x, y: nodePoints.br.y })
        .finish();

      return {
        left: {
          path: leftPath,
          points: { startPoint: leftStart, endPoint: leftEnd },
        },
        right: {
          path: barPath,
          points: { startPoint: nodePoints.tr, endPoint: nodePoints.br },
        },
        bracketWidth,
        padding: bracketWidth + 2,
      };
    },
    staticWidth: 6,
  },
  ket: {
    path: (nodePoints, addBracketWidth = 0) => {
      const { y: height } = nodePoints.bl;

      const minBracketWidth = 3;
      const maxBracketWidth = 10;
      const baseWidth = Math.floor(minBracketWidth + (height * 2) / 10 - 1);
      const bracketWidth = Math.min(maxBracketWidth, baseWidth) + addBracketWidth;

      const barPath = drawPath()
        .moveTo({ x: nodePoints.tl.x, y: nodePoints.tl.y })
        .lineTo({ x: nodePoints.bl.x, y: nodePoints.bl.y })
        .finish();

      const rightStart = { x: nodePoints.tr.x - bracketWidth, y: nodePoints.tr.y };
      const rightMid = { x: nodePoints.rc.x, y: nodePoints.rc.y };
      const rightEnd = { x: nodePoints.br.x - bracketWidth, y: nodePoints.br.y };

      const rightPath = drawPath().moveTo(rightStart).lineTo(rightMid).lineTo(rightEnd).finish();

      return {
        left: {
          path: barPath,
          points: { startPoint: nodePoints.tl, endPoint: nodePoints.bl },
        },
        right: {
          path: rightPath,
          points: { startPoint: rightStart, endPoint: rightEnd },
        },
        bracketWidth,
        padding: bracketWidth + 2,
      };
    },
    staticWidth: 6,
  },
};

function getBodyPath(left: SymbolPath, right: SymbolPath) {
  const bodyExtension = 0.5;

  return drawPath(0, 0, `${left.path} ${right.path}`)
    .lineTo({
      x: right.points.endPoint.x,
      y: right.points.endPoint.y + bodyExtension,
    })
    .lineTo({
      x: left.points.endPoint.x,
      y: left.points.endPoint.y + bodyExtension,
    })
    .lineTo({
      x: left.points.startPoint.x,
      y: left.points.startPoint.y - bodyExtension,
    })
    .lineTo({
      x: right.points.startPoint.x,
      y: right.points.startPoint.y - bodyExtension,
    })
    .finish();
}

function getGroupSymbolPaths(symbol?: OpeningSymbol | ClosingSymbol) {
  for (const key of Object.keys(groupsJson) as GroupTypeValue[]) {
    if (
      groupsJson[key].openingSymbol.symbol === symbol ||
      groupsJson[key].closingSymbol.symbol === symbol
    ) {
      return bracketsSvgPaths[key];
    }
  }

  return bracketsSvgPaths.round;
}

type Options = Partial<{
  addBracketWidth: number;
  openingSymbol: OpeningSymbol;
  closingSymbol: ClosingSymbol;
}>;

type Path = {
  path: string;
  bracketWidth: number;
  padding?: number;
};

type ReturnProps = {
  paths: {
    left: Path;
    right: Path;
    body: string;
  };
  viewBox: ViewBox;
};

export function getStaticBracketLayoutWidth(
  symbol: OpeningSymbol | ClosingSymbol | undefined,
): number {
  return getGroupSymbolPaths(symbol).staticWidth ?? DEFAULT_BRACKET_WIDTH;
}

export function useGetBracketsSvgPathsBySymbol(
  containerRef: React.RefObject<HTMLDivElement | null>,
  node: NodeModel,
  { openingSymbol, closingSymbol, addBracketWidth = 0 }: Options,
): ReturnProps {
  const { nodePoints, viewBox } = useNodePoints(containerRef, node);

  const leftEntry = getGroupSymbolPaths(openingSymbol || closingSymbol);
  const rightEntry = getGroupSymbolPaths(closingSymbol || openingSymbol);

  const {
    left,
    bracketWidth: leftBracketWidth,
    padding: leftPadding,
  } = leftEntry.path(nodePoints, addBracketWidth);

  const {
    right,
    bracketWidth: rightBracketWidth,
    padding: rightPadding,
  } = rightEntry.path(nodePoints, addBracketWidth);

  return {
    paths: {
      left: {
        path: left.path,
        bracketWidth: leftBracketWidth,
        padding: leftPadding,
      },
      right: {
        path: right.path,
        bracketWidth: rightBracketWidth,
        padding: rightPadding,
      },
      body: getBodyPath(left, right),
    },
    viewBox,
  };
}
